import 'flatpickr/dist/flatpickr.css';

import { GlobalMessages } from '@shared/localization/messages';
import dayjs from 'dayjs';
import FlatpickrLanguages from 'flatpickr/dist/l10n';
import type { Ref } from 'react';
import { useMemo } from 'react';
import Flatpickr from 'react-flatpickr';

import type { eLanguage } from '@/context/localization';
import { useLocalization } from '@/context/localization';
import { dayjsLocale } from '@/dayjs';
import { useMessages } from '@/hooks/messages';
import yearDropdownPluginFactory from '@/utils/flatpickr';

interface DatePickerProps {
  id?: string;
  className?: string;
  readOnly?: boolean;
  value?: Date | null;
  placeholder?: string;
  onChange: (value: Date | null) => void;
  onBlur?: () => void;
  ref?: Ref<HTMLDivElement>;
}

export const DatePicker = ({
  id,
  className,
  readOnly,
  value,
  placeholder,
  onChange,
  onBlur,
  ref,
}: DatePickerProps) => {
  const globalMessages = useMessages(GlobalMessages);
  const {
    state: { locale },
  } = useLocalization();

  const yearDropdownPlugin = useMemo(
    () => yearDropdownPluginFactory({ minYear: 2000, maxYear: 2100 }),
    [],
  );

  const handleDateChange = (dates: Date[]) => {
    const newDate = dates.length > 0 ? dates[0] : null;
    onChange(newDate);
  };

  return (
    <div ref={ref}>
      <Flatpickr
        id={id}
        className={`${className ?? ''}`}
        value={value ?? undefined}
        onChange={handleDateChange}
        options={{
          locale: FlatpickrLanguages[locale.split('-')[0] as keyof typeof FlatpickrLanguages],
          dateFormat: getFlatpickrDateFormat(locale),
          formatDate: (date) => dayjs(date).format(getLocaleDateFormat(locale)),
          plugins: [yearDropdownPlugin],
          allowInput: !readOnly,
          clickOpens: !readOnly,
        }}
        placeholder={placeholder ?? globalMessages.chooseDate()}
        onClose={handleDateChange}
        onBlur={onBlur}
      />
    </div>
  );
};

function getLocaleDateFormat(locale: eLanguage): string {
  return dayjs().locale(dayjsLocale(locale)).localeData().longDateFormat('L');
}

function getFlatpickrDateFormat(locale: eLanguage): string {
  return getLocaleDateFormat(locale).replace('YYYY', 'Y').replace('MM', 'm').replace('DD', 'd');
}
